import { useMemo } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { LoadingState, ErrorState } from "@/components/ui/empty";
import { useApi } from "@/lib/api";
import { formatFitness } from "@/lib/format";
import type {
  ExperimentsResponse,
  RungsResponse,
} from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Ceiling board — best-of fitness per (rung, engine_version).
 *
 * The cloud Worker doesn't ship a /api/ceiling endpoint yet, so we derive
 * the matrix client-side from /api/experiments. Every (rung, engine) cell
 * shows the highest final_fitness across all completed runs, color-coded
 * against the rung's pass threshold.
 */
export function CeilingPage() {
  const experiments = useApi<ExperimentsResponse>("/api/experiments");
  const rungs = useApi<RungsResponse>("/api/rungs");

  const { rungList, engines, cells, thresholds } = useMemo(() => {
    const exps = experiments.data?.experiments ?? [];
    const rungList = (rungs.data?.rungs ?? [])
      .slice()
      .sort((a, b) => a.id.localeCompare(b.id));
    const thresholds = new Map<string, number>();
    for (const r of rungList) thresholds.set(r.id, r.fitness_threshold);

    const engineSet = new Set<string>();
    const cells = new Map<
      string,
      { best: number; expId: string }
    >(); // key: "rung::engine"

    for (const e of exps) {
      if (e.final_fitness === null || e.final_fitness === undefined) continue;
      engineSet.add(e.engine_version);
      const key = `${e.rung_id}::${e.engine_version}`;
      const prev = cells.get(key);
      if (!prev || e.final_fitness > prev.best) {
        cells.set(key, { best: e.final_fitness, expId: e.id });
      }
    }

    // Sort engines: prime versions numerically descending (v0.2.5 → v0.1.0)
    const engines = Array.from(engineSet).sort((a, b) =>
      versionCompare(b, a),
    );

    return { rungList, engines, cells, thresholds };
  }, [experiments.data, rungs.data]);

  if (experiments.loading || rungs.loading) return <LoadingState />;
  if (experiments.error) return <ErrorState error={experiments.error} />;
  if (rungs.error) return <ErrorState error={rungs.error} />;

  return (
    <>
      <PageHeader
        title="Ceiling Board"
        description="Best-of fitness per rung × engine, derived from completed experiments."
      />

      {engines.length === 0 ? (
        <EmptyCard />
      ) : (
        <Card className="overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {rungList.length} rungs × {engines.length} engine versions
            </CardTitle>
            <CardDescription className="text-xs">
              Cells show best final_fitness for that (rung, engine) pair.
              Green = passed, amber = below threshold, blank = no completed
              run. Click a cell to view the champion experiment.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="sticky left-0 z-10 bg-muted/30 px-3 py-2 text-left font-medium text-muted-foreground">
                      rung
                    </th>
                    <th className="px-3 py-2 text-right font-medium text-muted-foreground">
                      threshold
                    </th>
                    {engines.map((eng) => (
                      <th
                        key={eng}
                        className="px-3 py-2 text-center font-mono text-[10px] font-medium text-muted-foreground"
                      >
                        {eng}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rungList.map((r) => {
                    const threshold = thresholds.get(r.id) ?? 0;
                    return (
                      <tr key={r.id} className="border-b hover:bg-muted/20">
                        <td className="sticky left-0 z-10 bg-background px-3 py-2">
                          <div className="flex flex-col">
                            <span className="font-mono font-semibold">
                              {r.id}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              {r.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                          {threshold.toFixed(2)}
                        </td>
                        {engines.map((eng) => {
                          const cell = cells.get(`${r.id}::${eng}`);
                          if (!cell) {
                            return (
                              <td
                                key={eng}
                                className="px-3 py-2 text-center text-muted-foreground/30"
                              >
                                ·
                              </td>
                            );
                          }
                          const passed = cell.best >= threshold;
                          return (
                            <td key={eng} className="px-3 py-2 text-center">
                              <Link
                                to={`/experiments/${cell.expId}`}
                                className={cn(
                                  "inline-flex items-center justify-center gap-1 rounded border px-2 py-1 font-mono tabular-nums transition-colors",
                                  passed
                                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
                                    : "border-amber-500/30 bg-amber-500/5 text-amber-300 hover:bg-amber-500/15",
                                )}
                              >
                                {formatFitness(cell.best)}
                                {passed && (
                                  <CheckCircle2 className="h-3 w-3" />
                                )}
                              </Link>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}

function EmptyCard() {
  return (
    <Card>
      <CardContent className="py-12 text-center text-sm text-muted-foreground">
        No completed experiments yet — the ceiling board fills in as runs
        finish.
      </CardContent>
    </Card>
  );
}

/**
 * Rough semver-ish compare for strings like "prime-v0.2.5" and "proto-v0.1.0".
 * Strips the prefix and compares numeric parts.
 */
function versionCompare(a: string, b: string): number {
  const na = parseVersion(a);
  const nb = parseVersion(b);
  for (let i = 0; i < Math.max(na.length, nb.length); i++) {
    const ai = na[i] ?? 0;
    const bi = nb[i] ?? 0;
    if (ai !== bi) return ai - bi;
  }
  return a.localeCompare(b);
}

function parseVersion(s: string): number[] {
  const m = s.match(/(\d+(?:\.\d+)+)/);
  if (!m) return [];
  return m[1].split(".").map((n) => parseInt(n, 10));
}
